/**
 * Réglages métier saisis depuis le dashboard — contrat de `/api/settings`.
 *
 * Même règle que les autres fichiers de `shared/types` : importé par le back
 * et le front, jamais redéclaré.
 */

/** Taux de marge brute, en % du CA TTC. */
export const GROSS_MARGIN_MIN = 0;
export const GROSS_MARGIN_MAX = 100;

export interface BusinessSettings {
  /**
   * Taux de marge brute moyen de la boutique, en % du CA TTC : (CA − coût
   * des produits vendus − port payé) ÷ CA. Sert au « ROI sur marge ».
   * `null` tant que personne ne l'a renseigné.
   */
  grossMarginPercent: number | null;
  /**
   * Coût d'envoi moyen d'une commande Shopify (transporteur, emballage), en €
   * HT. Sert aux « Shipping costs » : ce coût × nombre de commandes. `null`
   * tant que personne ne l'a renseigné.
   */
  shippingCostPerOrder: number | null;
  /**
   * Ancien réglage unique du coût de production d'un kit, en € HT. Remplacé
   * par les périodes de `/api/settings/kit-costs` (migration 024) ; gardé pour
   * ne pas casser un client plus ancien.
   */
  kitProductionCost: number | null;
  /**
   * Frais Shopify Payments : pourcentage du CA TTC encaissé, et part fixe en €
   * par commande (ex. 1,6 % + 0,25 €). La boutique n'encaisse pas via Stripe.
   * Entrent dans la MC1 de la page Shopify et la MC2 du Résumé. `null` tant
   * que personne ne les a renseignés.
   */
  shopPaymentFeePercent: number | null;
  shopPaymentFeePerOrder: number | null;
  /** Dernière modification (ISO 8601), ou `null` jamais réglé. */
  updatedAt: string | null;
}

export interface BusinessSettingsResponse {
  settings: BusinessSettings;
}

/** Corps de `PUT /api/settings`. Champ absent : inchangé ; `null` : effacé. */
export interface BusinessSettingsInput {
  grossMarginPercent?: number | null;
  shippingCostPerOrder?: number | null;
  kitProductionCost?: number | null;
  shopPaymentFeePercent?: number | null;
  shopPaymentFeePerOrder?: number | null;
}

/** Coût d'envoi par commande, en € HT. */
export const SHIPPING_COST_MAX = 1000;

/** Frais Shopify Payments : % du TTC, et € par commande. */
export const SHOP_PAYMENT_FEE_PERCENT_MAX = 20;
export const SHOP_PAYMENT_FEE_PER_ORDER_MAX = 10;

/** Frais de paiement de la boutique, tels que réglés : `null` tant que le taux manque. */
export interface ShopPaymentFee {
  percent: number;
  perOrder: number;
}

/** Frais sur un lot de commandes : % du TTC encaissé (avant remboursements : Shopify ne les rend pas) + fixe × commandes. */
export const shopPaymentFeeOf = (fee: ShopPaymentFee, figures: { grossRevenue: number; orders: number }): number =>
  Math.round(((figures.grossRevenue * fee.percent) / 100 + figures.orders * fee.perOrder) * 100) / 100;

/** Coût de production d'un kit, en € HT. */
export const KIT_PRODUCTION_COST_MAX = 10000;

// -----------------------------------------------------------------------------
// Coût de revient d'un kit, par période — `/api/settings/kit-costs`
// -----------------------------------------------------------------------------

/**
 * Un coût de revient (€ HT par kit) valable du `startDate` au `endDate`
 * inclus — `null` : toujours en cours. Les périodes ne se chevauchent pas.
 * Chaque commande prend le coût en vigueur à sa date.
 */
export interface KitCostPeriod {
  id: number;
  startDate: string;
  endDate: string | null;
  cost: number;
  note: string | null;
}

export interface KitCostPeriodsResponse {
  /** De la plus récente à la plus ancienne. */
  periods: KitCostPeriod[];
}

// -----------------------------------------------------------------------------
// Coût d'un article saisi depuis le dashboard — `/api/settings/article-costs`
// -----------------------------------------------------------------------------

/**
 * Un article = une variante Shopify vendue sur les douze derniers mois. Son
 * coût unitaire (€ HT) est, par priorité : celui saisi ici (`unitCost`), sinon
 * le « coût par article » Shopify (`shopifyCost`), sinon pour un pack le coût
 * déduit (`derivedCost`). Un kit Piano LED vendu seul prend d'abord sa
 * période de coût.
 */
export interface ArticleCost {
  variantId: string;
  title: string;
  variantTitle: string | null;
  category: 'kit' | 'piano' | 'subscription' | 'other';
  unitsLast12Months: number;
  lastSoldAt: string;
  shopifyCost: number | null;
  /** Coût saisi dans le dashboard, `null` si aucun. */
  unitCost: number | null;
  /**
   * Pack « piano + Piano LED » sans coût propre : coût déduit = kit de la
   * période en cours + piano vendu seul. `null` pour les autres articles, ou
   * si le piano n'a pas de coût.
   */
  derivedCost: number | null;
  note: string | null;
  updatedAt: string | null;
}

export interface ArticleCostsResponse {
  /** Les plus vendus d'abord. */
  articles: ArticleCost[];
}

/** Corps de `PUT /api/settings/article-costs/:variantId`. `unitCost: null` retire le coût saisi. */
export interface ArticleCostInput {
  unitCost: number | null;
  note?: string | null;
}

/** Corps de `POST` et `PUT /api/settings/kit-costs`. */
export interface KitCostPeriodInput {
  startDate: string;
  endDate: string | null;
  cost: number;
  note?: string | null;
}

// -----------------------------------------------------------------------------
// Retenues sur le CA des abonnements — `/api/settings/deductions`
// -----------------------------------------------------------------------------

/**
 * Une part du CA des abonnements à déduire : Sacem, commission… Libellé,
 * pourcentage et base, saisis dans le dashboard. Le « Final HT » de la page
 * Souscriptions les retranche toutes.
 */
export type DeductionBase = 'ttc' | 'ht';

export const DEDUCTION_LABEL_MAX_LENGTH = 60;

export interface SubscriptionDeduction {
  id: number;
  label: string;
  /** En % de la base, 0 à 100. */
  percent: number;
  /** CA TTC ou CA HT des abonnements. */
  base: DeductionBase;
}

export interface SubscriptionDeductionsResponse {
  deductions: SubscriptionDeduction[];
}

/** Corps de `POST` et `PUT /api/settings/deductions`. */
export interface SubscriptionDeductionInput {
  label: string;
  percent: number;
  base: DeductionBase;
}
